import React, { useState } from "react";
import { Send, CheckCircle2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const inputClass =
  "w-full border-[3px] border-black bg-white px-4 py-3 font-mono text-sm text-black placeholder:text-black/50 focus:outline-none focus:ring-0 focus:border-black focus:shadow-[4px_4px_0px_0px_#00FF41] transition-shadow";

const labelClass =
  "mb-2 block font-mono text-[11px] font-bold uppercase tracking-widest text-black";

const ConnectForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", number: "", email: "", reason: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleChange = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim() || !form.number.trim() || !form.email.trim()) {
      toast({
        title: "Missing required fields",
        description: "Name, Number and Email are required.",
      });
      return;
    }

    setLoading(true);

    const encode = (data) => {
      return Object.keys(data)
        .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
        .join("&");
    };

    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "contact", ...form }),
      });

      setDone(true);
      toast({
        title: "Message received",
        description: "Thanks — I'll get back to you shortly.",
      });

      setForm({ name: "", number: "", email: "", reason: "" });
      setTimeout(() => setDone(false), 3500);

    } catch (err) {
      toast({
        title: "Submission failed",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="connect" className="border-b-[3px] border-black bg-[#E5E5E5]">
      <div className="mx-auto max-w-7xl px-6 py-16 md:py-24 lg:px-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="mb-3 inline-block border-[3px] border-black bg-[#00FF41] px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-widest text-black shadow-[4px_4px_0px_0px_#000000]">
              &gt; connect.with_me()
            </div>
            <h2 className="font-heading text-4xl font-extrabold leading-tight tracking-tight text-black sm:text-5xl">
              Let&apos;s talk <br /> markets, ops <br /> or startups.
            </h2>
            <p className="mt-6 max-w-md font-mono text-sm leading-relaxed text-black">
              Drop your details below and tell me why you&apos;re reaching out.
              I read every message personally.
            </p>

            <div className="mt-8 border-[3px] border-black bg-white p-4 shadow-[6px_6px_0px_0px_#000000]">
              <div className="font-mono text-[11px] font-bold uppercase tracking-widest text-black">
                response_time.log
              </div>
              <div className="mt-2 font-mono text-sm text-black">
                <span className="mr-2 inline-block h-2 w-2 bg-[#00FF41] border-[1.5px] border-black" />
                Usually replies within 24–48 hours
              </div>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="relative lg:col-span-7"
            noValidate
            name="contact"
            method="POST"
            data-netlify="true"
          >
            <input type="hidden" name="form-name" value="contact" />
            <div className="border-[3px] border-black bg-white shadow-[8px_8px_0px_0px_#000000]">
              <div className="flex items-center gap-2 border-b-[3px] border-black bg-[#E5E5E5] px-3 py-2">
                <span className="h-3 w-3 border-[2px] border-black bg-white" />
                <span className="h-3 w-3 border-[2px] border-black bg-white" />
                <span className="h-3 w-3 border-[2px] border-black bg-[#00FF41]" />
                <span className="ml-2 font-mono text-xs font-bold tracking-wider text-black">
                  contact_form.run
                </span>
              </div>

              <div className="grid grid-cols-1 gap-5 p-6 sm:p-8 md:grid-cols-2">
                <div className="md:col-span-1">
                  <label className={labelClass} htmlFor="name">
                    Name <span className="text-black">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className={inputClass}
                    required
                  />
                </div>

                <div className="md:col-span-1">
                  <label className={labelClass} htmlFor="number">
                    Number <span className="text-black">*</span>
                  </label>
                  <input
                    id="number"
                    name="number"
                    type="tel"
                    value={form.number}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    className={inputClass}
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass} htmlFor="email">
                    Email <span className="text-black">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@domain.com"
                    className={inputClass}
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass} htmlFor="reason">
                    Reason <span className="font-normal normal-case text-black/60">(optional)</span>
                  </label>
                  <textarea
                    id="reason"
                    name="reason"
                    rows={4}
                    value={form.reason}
                    onChange={handleChange}
                    placeholder="What would you like to discuss?"
                    className={`${inputClass} resize-none`}
                  />
                </div>

                <div className="md:col-span-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex w-full items-center justify-center gap-2 border-[3px] border-black bg-[#00FF41] px-6 py-3 font-mono text-sm font-bold uppercase tracking-wider text-black shadow-[4px_4px_0px_0px_#000000] transition-transform duration-150 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#000000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-[0px_0px_0px_0px_#000000] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                  >
                    {loading ? (
                      <>Sending…</>
                    ) : done ? (
                      <>
                        <CheckCircle2 className="h-4 w-4" strokeWidth={3} /> Sent
                      </>
                    ) : (
                      <>
                        Submit Message <Send className="h-4 w-4" strokeWidth={3} />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ConnectForm;
